import { Colors, Heading, Text } from '@amelio/ui-library';

import type { ReactNode } from 'react';

/**
 * The near-opaque inner card a panel sets its content on (a list of evaluations, a review, the
 * objectives figure). Paddings read the design system's `--space-*` tokens, so the panel keeps the
 * rhythm the rest of the profile uses rather than one of its own.
 */
export const EVALUATION_PANEL_INNER_SURFACE =
    'rounded-lg bg-white/90 px-(--space-lg) py-(--space-lg) lg:px-(--space-3xl) lg:py-(--space-4xl)';

/**
 * A panel title at the design system's `text-subtitle` (20px), rather than a library heading
 * variant, so every panel of the profile is titled at the same size.
 */
export const EVALUATION_PANEL_TITLE = 'text-subtitle leading-tight';

/**
 * The same surface, for a list of links: it keeps no padding of its own, so that every point of
 * the card falls on a row's link rather than beside it. The rows carry the padding instead, and
 * nothing here clips them -- a row now reaches the card's edge, and `FOCUS_RING` draws its ring
 * outside that edge.
 */
export const EVALUATION_PANEL_INNER_LIST = 'rounded-lg bg-white/90';

/**
 * One dated line of an inner list (manager or self-assessments): the rule between lines, and the
 * deeper padding the link takes at either end so the list breathes against the card's edges the
 * way `EVALUATION_PANEL_INNER_SURFACE` does.
 */
export const EVALUATION_LIST_ITEM =
    'border-b border-(--color-base-100) last:border-b-0 lg:first:[&>a]:pt-(--space-4xl) lg:last:[&>a]:pb-(--space-4xl)';

/**
 * The link that fills a list item: the date left, the name right. It carries the whole padding of
 * the surface around it -- so a click in the margin opens the evaluation like a click on the text.
 */
export const EVALUATION_LIST_ROW =
    'text-navy text-body-sm hover:text-primary flex w-full items-center justify-between gap-(--gap-gap-2xl) px-(--space-lg) py-(--space-lg) lg:px-(--space-3xl) font-medium';

interface EvaluationPanelProps {
    title: string;
    description?: string;
    children: ReactNode;
    /** Placement in the assessment tab's rows (`lg:self-start`, an offset); the panel sets no width. */
    className?: string;
    dataTestId?: string;
    /** A decorative picture resting on the card's top edge, right side; wide screens only. */
    illustration?: ReactNode;
    /**
     * A control on the title's line, right side, vertically centred on it (the objectives' period
     * filter). It wraps under the title when the line runs out of room; its width is its own.
     */
    headerAction?: ReactNode;
}

/**
 * The translucent card shared by the assessment tab's lower rows -- objectives, manager
 * assessments, self-assessment and 360 feedback: a title, an optional introduction, then the
 * panel's own content stacked underneath.
 */
export const EvaluationPanel = ({
    title,
    description,
    children,
    className = '',
    dataTestId,
    illustration,
    headerAction,
}: EvaluationPanelProps) => (
    <section
        className={`relative flex flex-col gap-(--gap-gap-2xl) rounded-xl border border-white bg-white/45 px-(--space-lg) pt-(--space-4xl) pb-(--space-2xl) backdrop-blur-sm lg:px-(--space-6xl) lg:pt-(--space-8xl) lg:pb-(--space-4xl) ${className}`}
        data-testid={dataTestId}
    >
        {illustration && (
            <div
                aria-hidden
                className={
                    'pointer-events-none absolute -top-14 right-(--space-2xl) hidden lg:block'
                }
            >
                {illustration}
            </div>
        )}
        <header className={'flex flex-col gap-(--gap-gap-lg)'}>
            <div className={'flex flex-wrap items-center justify-between gap-(--gap-gap-lg)'}>
                <Heading
                    level={3}
                    color={Colors.NAVY}
                    className={EVALUATION_PANEL_TITLE}
                >
                    {title}
                </Heading>
                {headerAction}
            </div>
            {description && (
                <Text
                    color={Colors.BASE_500}
                    className={'text-body-sm'}
                >
                    {description}
                </Text>
            )}
        </header>
        {children}
    </section>
);
